import { ActionReducerMap, createSelector, MetaReducer } from "@ngrx/store";
import { environment } from "../../../environments/environment";
import * as fromLogin from "./login/login.reducer";
import * as fromUsers from "./users/user.reducer";
import * as fromMessages from "./messages/message.reducer";
import * as fromParties from "./parties/parties.reducer";
import * as fromContracts from "./contracts/contracts.reducer";
import * as fromVehicles from "./vehicles/vehicles.reducer";
import * as fromCards from "./cards/cards.reducer";
import * as fromPrompts from "./prompts/prompts.reducer";
import * as fromProperties from "./properties/properties.reducer";
import * as fromCategories from "./categories/categories.reducer";
import * as fromMenus from "./menus/menu.reducer";
import * as fromReports from "./reports/reports.reducer";
import * as fromSites from "./sites/sites.reducer";
import * as fromMovements from "./movements/movements.reducer";
import * as fromFuelStocks from "./fuelStocks/fuelStocks.reducer";
import * as fromDeliveries from "./deliveries/deliveries.reducer";
import * as fromWarehouses from "./warehouse/warehouse.reducer";
import * as fromDashboard from "./dashboard/dashboard.reducer";
import * as fromProfessionalFuel from "./professional-fuel/professional-fuel.reducer";
import * as fromTenant from "./tenant/tenant.reducer";
import * as fromGrades from "./grades/grades.reducer";
import * as fromProducts from "./products/product.reducer";
import * as fromProductGroup from "./productGroups/productGroups.reducer";
import * as fromPosCard from "./pos-cards/pos-cards.reducer";
import * as fromDictionary from "./dictionary/dictionary.reducer";
import * as fromServiceModes from "./service-modes/service-modes.reducer";
import * as fromFuelingMode from "./fueling-modes/fueling-modes.reducer";
import * as fromTankGauges from "./tank-gauges/tank-gauges.reducer";
import * as fromLocales from "./locales/locales.reducer";
export interface AppState {
  login: fromLogin.LoginState;
  users: fromUsers.UsersState;
  messages: fromMessages.MessagesState;
  parties: fromParties.PartiesState;
  contracts: fromContracts.ContractsState;
  vehicles: fromVehicles.VehiclesState;
  cards: fromCards.CardsState;
  prompts: fromPrompts.PromptsState;
  properties: fromProperties.PropertiesState;
  categories: fromCategories.CategoriesState;
  menus: fromMenus.MenusState;
  reports: fromReports.ReportsState;
  sites: fromSites.SitesState;
  movements: fromMovements.MovementsState;
  fuelStocks: fromFuelStocks.FuelStocksState;
  deliveries: fromDeliveries.DeliveriesState;
  warehouses: fromWarehouses.WarehousesState;
  dashboards: fromDashboard.DashboardState;
  professionalFuel: fromProfessionalFuel.ProfessionalFuelState;
  tenant: fromTenant.TenantState;
  grades: fromGrades.GradeState;
  products: fromProducts.ProductState;
  groups: fromProductGroup.ProductGroupState;
  posCards: fromPosCard.PosCardsState;
  dictionary: fromDictionary.DictionaryState;
  serviceModes: fromServiceModes.ServiceModeState;
  fuelingMode: fromFuelingMode.FuelingModeState;
  tankGauge: fromTankGauges.TankGaugeState;
  locales: fromLocales.LocaleState;
}

export const reducers: ActionReducerMap<AppState> = {
  login: fromLogin.reducer,
  users: fromUsers.reducer,
  messages: fromMessages.reducer,
  parties: fromParties.reducer,
  contracts: fromContracts.reducer,
  vehicles: fromVehicles.reducer,
  cards: fromCards.reducer,
  prompts: fromPrompts.reducer,
  properties: fromProperties.reducer,
  categories: fromCategories.reducer,
  menus: fromMenus.reducer,
  reports: fromReports.reducer,
  sites: fromSites.reducer,
  movements: fromMovements.reducer,
  fuelStocks: fromFuelStocks.reducer,
  deliveries: fromDeliveries.reducer,
  warehouses: fromWarehouses.reducer,
  dashboards: fromDashboard.reducer,
  professionalFuel: fromProfessionalFuel.reducer,
  tenant: fromTenant.reducer,
  grades: fromGrades.reducer,
  products: fromProducts.reducer,
  groups: fromProductGroup.reducer,
  posCards: fromPosCard.reducer,
  dictionary: fromDictionary.reducer,
  serviceModes: fromServiceModes.reducer,
  fuelingMode: fromFuelingMode.reducer,
  tankGauge: fromTankGauges.reducer,
  locales: fromLocales.reducer,
};

// Login State
export const loginState = (state: AppState) => state.login;
export const loginStateisAuthenticated = createSelector(
  loginState,
  (login) => login.isAuthenticated
);
export const loginStateCurrentUser = createSelector(
  loginState,
  (login) => login.currentUser
);
export const loginStateIsLoading = createSelector(
  loginState,
  (login) => login.isLoading
);
export const loginStateShowExpired = createSelector(
  loginState,
  (login) => login.showExpired
);
export const loginStatePasswordRecovered = createSelector(
  loginState,
  (login) => login.passwordRecovered
);
export const loginStatePasswordReseted = createSelector(
  loginState,
  (login) => login.passwordReseted
);
export const loginStateLocaleList = createSelector(
  loginState,
  (login) => login.localesList
);

export const dashboardState = (state: AppState) => state.dashboards;
export const dashboardAverageStateList = createSelector(
  dashboardState,
  (dashboard) => dashboard.averages
);
export const dashboardTransactionStateList = createSelector(
  dashboardState,
  (dashboard) => dashboard.transactions
);
export const dashboardIsLoadingState = createSelector(
  dashboardState,
  (dashboard) => dashboard.isLoading
);
export const dashboardIsConsumedProductLoadingState = createSelector(
  dashboardState,
  (dashboard) => dashboard.isConsumedProductLoading
);

//POS Cards
export const posCardState = (state: AppState) => state.posCards;
export const posCardsStateList = createSelector(
  posCardState,
  (posCards) => posCards.posCardList
);
export const posCardsStateExcelList = createSelector(
  posCardState,
  (posCards) => posCards.posCardExcelList
);
export const posCardsIsLoadingState = createSelector(
  posCardState,
  (posCards) => posCards.isLoading
);
export const posCardsTotalState = createSelector(
  posCardState,
  (posCards) => posCards.totalCards
);
export const getPosCardState = createSelector(
  posCardState,
  (posCards) => posCards.posCard
);
export const posCardConfigState = createSelector(
  posCardState,
  (posCards) => posCards.posCardTableConfig
);

// Users State
export const userState = (state: AppState) => state.users;
export const usersStateUsersList = createSelector(
  userState,
  (users) => users.userList
);
export const usersStateUsersExcelList = createSelector(
  userState,
  (users) => users.userExcelList
);
export const usersStateTotalUsers = createSelector(
  userState,
  (users) => users.totalUsers
);
export const usersStateTableConfig = createSelector(
  userState,
  (users) => users.userTableConfig
);
export const usersStateCreated = createSelector(
  userState,
  (users) => users.created
);
export const usersStateUser = createSelector(userState, (users) => users.user);
export const usersStateUserProfiles = createSelector(
  userState,
  (users) => users.userProfilesList
);
export const usersStateLocaleList = createSelector(
  userState,
  (users) => users.localesList
);
export const usersStateListTotalRecords = createSelector(
  userState,
  (users) => users.totalRecords
);
export const usersStateIsLoading = createSelector(
  userState,
  (users) => users.isLoading
);
export const userStatePasswordChanged = createSelector(
  userState,
  (users) => users.passwordChanged
);

// Messages State
export const messagesState = (state: AppState) => state.messages;
export const messagesStateGetSnackbar = createSelector(
  messagesState,
  (messages) => messages.snackbar
);
export const messagesStateGetMessage = createSelector(
  messagesState,
  (messages) => messages.appMessage
);
export const messagesStateGetMessageResetFlag = createSelector(
  messagesState,
  (messages) => messages.resetMessage
);
export const messagesStateClearMessages = createSelector(
  messagesState,
  (messages) => messages.clearMessages
);

// Parties State
export const partiesState = (state: AppState) => state.parties;
export const partiesStateSalutations = createSelector(
  partiesState,
  (parties) => parties.salutations
);
export const partiesStateCustomerEmployeeList = createSelector(
  partiesState,
  (parties) => parties.employeesByCustomer
);
export const partiesStateCountries = createSelector(
  partiesState,
  (parties) => parties.countries
);
export const loadedPartyState = createSelector(
  partiesState,
  (parties) => parties.party
);
export const loadedCustomerVehiclesListState = createSelector(
  partiesState,
  (parties) => parties.vehicleListByCustomer
);
export const loadedCustomerPartyState = createSelector(
  partiesState,
  (parties) => parties.customerParty
);
export const loadedSupplierPartyState = createSelector(
  partiesState,
  (parties) => parties.supplierParty
);
export const loadedEmployeePartyState = createSelector(
  partiesState,
  (parties) => parties.employeeParty
);
export const partiesStateCreatedParty = createSelector(
  partiesState,
  (parties) => parties.created
);
export const loadedCategoriesPartyState = createSelector(
  partiesState,
  (parties) => parties.categories
);
export const loadedPropertiesPartyState = createSelector(
  partiesState,
  (parties) => parties.properties
);
export const loadedOptionalCategoriesPartyState = createSelector(
  partiesState,
  (parties) => parties.optionalCategories
);
export const loadedOptionalPropertiesPartyState = createSelector(
  partiesState,
  (parties) => parties.optionalProperties
);
export const loadedCustomerPartyListState = createSelector(
  partiesState,
  (parties) => parties.customerPartyList
);
export const loadedCategoriesListPartyState = createSelector(
  partiesState,
  (parties) => parties.categoriesList
);
export const loadedPropertiesListPartyState = createSelector(
  partiesState,
  (parties) => parties.propertiesList
);
export const getLoadingPartyState = createSelector(
  partiesState,
  (parties) => parties.isLoading
);
export const getLoadingCustomerSearchState = createSelector(
  partiesState,
  (parties) => parties.isLoadingCustomerSearch
);
export const loadedEmployeePartyListState = createSelector(
  partiesState,
  (parties) => parties.employeePartyList
);
export const loadedSupplierPartyListState = createSelector(
  partiesState,
  (parties) => parties.supplierPartyList
);
export const loadedEmployeePartyListExcelState = createSelector(
  partiesState,
  (parties) => parties.excelEmployeeList
);
export const loadedSupplierPartyListExcelState = createSelector(
  partiesState,
  (parties) => parties.excelSupplierList
);
export const loadedCustomerPartyListExcelState = createSelector(
  partiesState,
  (parties) => parties.excelCustomerList
);
export const loadedEmployeeListTotalState = createSelector(
  partiesState,
  (parties) => parties.totalEmployees
);
export const loadedSupplierListTotalState = createSelector(
  partiesState,
  (parties) => parties.totalSuppliers
);
export const loadedCustomerPartyListTotalState = createSelector(
  partiesState,
  (parties) => parties.totalCustomers
);
export const loadedCustomersTableConfig = createSelector(
  partiesState,
  (parties) => parties.customerTableConfig
);
export const loadedEmployeesTableConfig = createSelector(
  partiesState,
  (parties) => parties.employeeTableConfig
);
export const loadedSuppliersTableConfig = createSelector(
  partiesState,
  (parties) => parties.supplierTableConfig
);

// Contracts State
export const contractsState = (state: AppState) => state.contracts;
export const contractsStatePlafondRules = createSelector(
  contractsState,
  (contracts) => contracts.plafondRules
);
export const contractsStatePlafondMeasurements = createSelector(
  contractsState,
  (contracts) => contracts.plafondMeasurements
);
export const loadedContractState = createSelector(
  contractsState,
  (contracts) => contracts.contract
);
export const contractsStateCreatedContract = createSelector(
  contractsState,
  (contracts) => contracts.created
);
export const loadedContractsListState = createSelector(
  contractsState,
  (contracts) => contracts.contractList
);
export const loadedContractCardsListState = createSelector(
  contractsState,
  (contracts) => contracts.cardList
);
export const getLoadingContractsState = createSelector(
  contractsState,
  (contracts) => contracts.isLoading
);
export const loadedContractMovementListState = createSelector(
  contractsState,
  (contracts) => contracts.contractMovementList
);
export const contractsStateProductGroupsListState = createSelector(
  contractsState,
  (contracts) => contracts.productGroups
);
export const contractsStateSitesListState = createSelector(
  contractsState,
  (contracts) => contracts.sites
);
export const contractsLimitIntervalsListState = createSelector(
  contractsState,
  (contracts) => contracts.limitIntervals
);
export const contractsLimitMeasurementsListState = createSelector(
  contractsState,
  (contracts) => contracts.limitMeasurements
);
export const contractsExcelListState = createSelector(
  contractsState,
  (contracts) => contracts.contractExcelList
);
export const contractsTotalState = createSelector(
  contractsState,
  (contracts) => contracts.totalContracts
);
export const contractsStateTableConfig = createSelector(
  contractsState,
  (contracts) => contracts.contractTableConfig
);

// Vehicles State
export const vehiclesState = (state: AppState) => state.vehicles;
export const loadedVehicleState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.vehicle
);
export const vehicleStateCreatedVehicle = createSelector(
  vehiclesState,
  (vehicles) => vehicles.created
);
export const loadedVehiclesListState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.vehicleList
);
export const loadedVehicleCardsListState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.cardList
);
export const getLoadingVehiclesState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.isLoading
);
export const loadedCategoriesVehiclesState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.categories
);
export const loadedPropertiesVehiclesState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.properties
);
export const loadedOptionalCategoriesVehiclesState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.optionalCategories
);
export const loadedOptionalPropertiesVehiclesState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.optionalProperties
);
export const loadedCategoriesListVehiclesState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.categoriesList
);
export const loadedPropertiesListVehiclesState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.propertiesList
);
export const vehiclesStateBrands = createSelector(
  vehiclesState,
  (vehicles) => vehicles.brandList
);
export const vehiclesStateModels = createSelector(
  vehiclesState,
  (vehicles) => vehicles.modelList
);
export const vehiclesStateVersions = createSelector(
  vehiclesState,
  (vehicles) => vehicles.versionList
);
export const vehiclesStateMotorizations = createSelector(
  vehiclesState,
  (vehicles) => vehicles.motorizationList
);
export const loadedCustomerVehicleListState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.customerList
);
export const vehiclesStateProductGroupsListState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.productGroupList
);
export const vehiclesStateOdometerTypeListState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.odometerTypeList
);
export const vehiclesStateConsumptionRulesListState = createSelector(
  vehiclesState,
  (vehicles) => vehicles.consumptionRuleList
);
export const vehiclesStateVehicleAverages = createSelector(
  vehiclesState,
  (vehicles) => vehicles.vehicleAverages
);
export const vehiclesStateTransactionList = createSelector(
  vehiclesState,
  (vehicles) => vehicles.vehicleTransactions
);
export const vehiclesStateExcelList = createSelector(
  vehiclesState,
  (vehicles) => vehicles.vehicleExcelList
);
export const vehiclesStateTotalVehicles = createSelector(
  vehiclesState,
  (vehicles) => vehicles.totalVehicles
);
export const vehiclesStateTableConfig = createSelector(
  vehiclesState,
  (vehicles) => vehicles.vehicleTableConfig
);

// Cards State
export const cardsState = (state: AppState) => state.cards;
export const loadedCardPlafondState = createSelector(
  cardsState,
  (cards) => cards.cardPlafond
);
export const loadedCardSystemListState = createSelector(
  cardsState,
  (cards) => cards.cardSystems
);
export const loadCardLayoutListState = createSelector(
  cardsState,
  (cards) => cards.cardLayouts
);
export const loadCardCustomerContractsListState = createSelector(
  cardsState,
  (cards) => cards.contractList
);
export const loadCardCustomerEmployeesListState = createSelector(
  cardsState,
  (cards) => cards.employeePartyList
);
export const loadCardCustomerVehiclesListState = createSelector(
  cardsState,
  (cards) => cards.vehiclesList
);
export const LoadCardState = createSelector(cardsState, (cards) => cards.card);
export const loadedCardCreatedCardState = createSelector(
  cardsState,
  (cards) => cards.created
);
export const LoadCardFilteredListState = createSelector(
  cardsState,
  (cards) => cards.cardList
);
export const cardsLimitIntervalsListState = createSelector(
  cardsState,
  (cards) => cards.limitIntervals
);
export const cardsLimitMeasurementsListState = createSelector(
  cardsState,
  (cards) => cards.limitMeasurements
);
export const cardsTransactionsListState = createSelector(
  cardsState,
  (cards) => cards.transactions
);
export const getLoadingCardsState = createSelector(
  cardsState,
  (cards) => cards.isLoading
);
export const getCardsExcelState = createSelector(
  cardsState,
  (cards) => cards.cardExcelList
);
export const getTotalCardsState = createSelector(
  cardsState,
  (cards) => cards.totalCards
);
export const loadedCardTableConfig = createSelector(
  cardsState,
  (cards) => cards.cardTableConfig
);

// Prompts State
export const promptsState = (state: AppState) => state.prompts;
export const loadedPromptsListState = createSelector(
  promptsState,
  (prompts) => prompts.prompts
);
export const loadedPromptsDataTypeListState = createSelector(
  promptsState,
  (prompts) => prompts.dataTypes
);
export const loadedPromptsCatergoriesListState = createSelector(
  promptsState,
  (prompts) => prompts.categories
);
export const loadedPromptState = createSelector(
  promptsState,
  (prompts) => prompts.prompt
);
export const createdPromptState = createSelector(
  promptsState,
  (prompts) => prompts.created
);
export const loadedExcelPromptsState = createSelector(
  promptsState,
  (prompts) => prompts.excelPrompts
);
export const loadedPromptsTotaltate = createSelector(
  promptsState,
  (prompts) => prompts.totalPrompts
);
export const loadedPromptsLoadingtate = createSelector(
  promptsState,
  (prompts) => prompts.isLoading
);
export const loadedPromptsTableConfig = createSelector(
  promptsState,
  (prompts) => prompts.promptsTableConfig
);

// Properties State
export const propertiesState = (state: AppState) => state.properties;
export const loadedPropertiesListState = createSelector(
  propertiesState,
  (properties) => properties.properties
);
export const loadedPropertiesUsageListState = createSelector(
  propertiesState,
  (properties) => properties.usages
);
export const loadedPropertiesDataTypeListState = createSelector(
  propertiesState,
  (properties) => properties.dataTypes
);
export const loadedPropertyState = createSelector(
  propertiesState,
  (properties) => properties.property
);
export const createdPropertyState = createSelector(
  propertiesState,
  (properties) => properties.created
);
export const LoadedExcelPropertiesState = createSelector(
  propertiesState,
  (properties) => properties.excelProperties
);
export const LoadedPropertieIsLoadingState = createSelector(
  propertiesState,
  (properties) => properties.isLoading
);
export const LoadedPropertieTotalState = createSelector(
  propertiesState,
  (properties) => properties.totalProperties
);
export const loadedPropertiesTableConfig = createSelector(
  propertiesState,
  (properties) => properties.propertiesTableConfig
);

// Categories State
export const categoriesState = (state: AppState) => state.categories;
export const loadedCategoriesListState = createSelector(
  categoriesState,
  (categories) => categories.listCategories
);
export const loadedCategoryState = createSelector(
  categoriesState,
  (categories) => categories.category
);
export const createdCategoryState = createSelector(
  categoriesState,
  (categories) => categories.created
);
export const loadedCategoryUsagesState = createSelector(
  categoriesState,
  (categories) => categories.usages
);
export const loadedCategoryElementsState = createSelector(
  categoriesState,
  (categories) => categories.listCategoryElements
);
export const loadedCategoryLoadingState = createSelector(
  categoriesState,
  (categories) => categories.isLoading
);
export const loadedCategoryExcelListState = createSelector(
  categoriesState,
  (categories) => categories.categoriesExcelList
);
export const loadedCategoryTotaState = createSelector(
  categoriesState,
  (categories) => categories.totalCategories
);
export const loadedCategoriesTableConfig = createSelector(
  categoriesState,
  (categories) => categories.categoriesTableConfig
);

// Menus
export const menuState = (state: AppState) => state.menus;
export const loadedMenusState = createSelector(
  menuState,
  (menus) => menus.menus
);
export const isLoadingMenusState = createSelector(
  menuState,
  (menus) => menus.isLoading
);

// Reports State
export const reportsState = (state: AppState) => state.reports;
export const reportsStateReportsList = createSelector(
  reportsState,
  (reports) => reports.reportsList
);
export const reportsStateReport = createSelector(
  reportsState,
  (reports) => reports.report
);
export const reportsStateIsLoading = createSelector(
  reportsState,
  (reports) => reports.isLoading
);
export const reportsStateReportStatusList = createSelector(
  reportsState,
  (reports) => reports.reportStatusList
);
export const reportsStateExcelReportStatusList = createSelector(
  reportsState,
  (reports) => reports.reportStatusExcelList
);
export const reportsStateTotalReportStatus = createSelector(
  reportsState,
  (reports) => reports.totalReportStatus
);
export const reportsStateTableConfig = createSelector(
  reportsState,
  (reports) => reports.reportStatusTableConfig
);

// Sites State
export const sitesState = (state: AppState) => state.sites;
export const sitesStateIsLoading = createSelector(
  sitesState,
  (sites) => sites.isLoading
);
export const sitesStateSitesList = createSelector(
  sitesState,
  (sites) => sites.sitesList
);
export const sitesStateExcelSitesList = createSelector(
  sitesState,
  (sites) => sites.sitesExcelList
);
export const sitesStateTotalSites = createSelector(
  sitesState,
  (sites) => sites.totalSites
);
export const sitesStateTableConfig = createSelector(
  sitesState,
  (sites) => sites.sitesTableConfig
);
export const sitesStateEnabledSitesList = createSelector(
  sitesState,
  (sites) => sites.enabledSiteList
);
export const sitesStateCreated = createSelector(
  sitesState,
  (sites) => sites.created
);
export const siteLoadedState = createSelector(
  sitesState,
  (sites) => sites.site
);
export const siteRegionLoadedState = createSelector(
  sitesState,
  (sites) => sites.regions
);
export const siteProfileLoadedState = createSelector(
  sitesState,
  (sites) => sites.profiles
);
export const siteWarehousesLoadedState = createSelector(
  sitesState,
  (sites) => sites.warehouses
);
export const warehouseTypesLoadedState = createSelector(
  sitesState,
  (sites) => sites.warehouseTypes
);
export const pumpTypesLoadedState = createSelector(
  sitesState,
  (sites) => sites.pumpTypes
);
export const priceSignTypesLoadedState = createSelector(
  sitesState,
  (sites) => sites.priceSignTypes
);
export const sitePosTypesLoadedState = createSelector(
  sitesState,
  (sites) => sites.posTypes
);
export const siteDispensorsLoadedState = createSelector(
  sitesState,
  (sites) => sites.dispensers
);
export const siteFccsLoadedState = createSelector(
  sitesState,
  (sites) => sites.fccs
);
export const sitePosLoadedState = createSelector(
  sitesState,
  (sites) => sites.pos
);
export const siteFccDeviceSubTypesLoadedState = createSelector(
  sitesState,
  (sites) => sites.fccDeviceSubTypes
);
export const siteFuelPointLoadedState = createSelector(
  sitesState,
  (sites) => sites.fuelPoints
);
export const sitePriceSignsLoadedState = createSelector(
  sitesState,
  (sites) => sites.priceSigns
);
export const siteProductListLoadedState = createSelector(
  sitesState,
  (sites) => sites.productList
);
export const siteStoreProductListLoadedState = createSelector(
  sitesState,
  (sites) => sites.storeProductList
);
export const siteFuelProductListLoadedState = createSelector(
  sitesState,
  (sites) => sites.fuelProductList
);
export const siteGradeListLoadedState = createSelector(
  sitesState,
  (sites) => sites.gradeList
);
export const loadedCategoriesListSiteState = createSelector(
  sitesState,
  (sites) => sites.categoriesList
);
export const loadedPropertiesListSiteState = createSelector(
  sitesState,
  (sites) => sites.propertiesList
);
export const loadedDeviceWarehousePropertiesListSiteState = createSelector(
  sitesState,
  (sites) => sites.deviceWarehousePropertiesList
);
export const loadedDevicePosPropertiesListSiteState = createSelector(
  sitesState,
  (sites) => sites.devicePosPropertiesList
);
export const sitePeripheralLoadedState = createSelector(
  sitesState,
  (sites) => sites.peripherals
);
export const sitePeripheralDeviceSubTypesLoadedState = createSelector(
  sitesState,
  (sites) => sites.peripheralDeviceSubTypes
);
export const siteMultimediaLoadedState = createSelector(
  sitesState,
  (sites) => sites.multimediaDevices
);

// Movements State
export const movementState = (state: AppState) => state.movements;
export const movementStateList = createSelector(
  movementState,
  (movements) => movements.movementsList
);
export const getMovementState = createSelector(
  movementState,
  (movements) => movements.movement
);
export const movementsStateIsLoading = createSelector(
  movementState,
  (movements) => movements.isLoading
);
export const movementsExcelListState = createSelector(
  movementState,
  (movements) => movements.movementExcelList
);
export const movementsTotalSizeState = createSelector(
  movementState,
  (movements) => movements.totalMovements
);
export const movementsStateTableConfig = createSelector(
  movementState,
  (movements) => movements.movementTableConfig
);

// Fuel Stocks State
export const fuelStockState = (state: AppState) => state.fuelStocks;
export const fuelStocksStateFuelStocksList = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.fuelStockList
);
export const fuelStocksStateLastMeasurement = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.lastMeasurement
);
export const fuelStocksStateLastDelivery = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.lastMeasurement
);
export const fuelStocksStateFuelStock = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.fuelStock
);
export const fuelStocksStateCreatedFuelStock = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.created
);
export const fuelStocksStateListTotalRecords = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.totalRecords
);
export const fuelStocksStateIsLoading = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.isLoading
);
export const fuelStocksStockStats = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.stockStats
);
export const fuelStocksExcelLitState = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.fuelStockExcelList
);
export const totalFuelStocksState = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.totalFuelStocks
);
export const fuelStocksStateTableConfig = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.fuelStockTableConfig
);
export const fuelStocksMovementsState = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.fuelStockMovements
);
export const fuelStocksMovementsTableConfigState = createSelector(
  fuelStockState,
  (fuelStocks) => fuelStocks.fuelStockMovementsTableConfig
);

//Deliveries State
export const deliveriesState = (state: AppState) => state.deliveries;
export const loadDeliviriesList = createSelector(
  deliveriesState,
  (deliveries) => deliveries.deliveryList
);
export const loadMeasurementList = createSelector(
  deliveriesState,
  (deliveries) => deliveries.measurementList
);
export const loadDebitList = createSelector(
  deliveriesState,
  (deliveries) => deliveries.debitList
);
export const loadDeliveryProduct = createSelector(
  deliveriesState,
  (deliveries) => deliveries.product
);
export const deliveriesStateCreatedDelivery = createSelector(
  deliveriesState,
  (deliveries) => deliveries.created
);
export const loadDelivery = createSelector(
  deliveriesState,
  (deliveries) => deliveries.delivery
);
export const deliveriesStateIsLoading = createSelector(
  deliveriesState,
  (deliveries) => deliveries.isLoading
);
export const deliveriesStateExcelList = createSelector(
  deliveriesState,
  (deliveries) => deliveries.deliveryExcelList
);
export const measurementsStateExcelList = createSelector(
  deliveriesState,
  (deliveries) => deliveries.measurementExcelList
);
export const debitStateExcelList = createSelector(
  deliveriesState,
  (deliveries) => deliveries.debitExcelList
);
export const deliveriesStateTotal = createSelector(
  deliveriesState,
  (deliveries) => deliveries.totalDeliveries
);
export const measurementsStateTotal = createSelector(
  deliveriesState,
  (deliveries) => deliveries.totalMeasurements
);
export const debitsStateTotal = createSelector(
  deliveriesState,
  (deliveries) => deliveries.totalDebits
);
export const deliveriesStateTableConfig = createSelector(
  deliveriesState,
  (deliveries) => deliveries.deliveryTableConfig
);
export const measurementsStateTableConfig = createSelector(
  deliveriesState,
  (deliveries) => deliveries.measurementTableConfig
);
export const debitsStateTableConfig = createSelector(
  deliveriesState,
  (deliveries) => deliveries.debitTableConfig
);

//Warehouses State
export const warehousesState = (state: AppState) => state.warehouses;
export const loadWarehousesBySiteState = createSelector(
  warehousesState,
  (warehouses) => warehouses.warehousesBySite
);

export const gradesState = (state: AppState) => state.grades;
export const loadedGradeListState = createSelector(
  gradesState,
  (grades) => grades.grades
);
export const loadedGradesListState = createSelector(
  gradesState,
  (grades) => grades.gradesList
);
export const loadedGradeExcelListState = createSelector(
  gradesState,
  (grades) => grades.gradesExcel
);
export const loadedGradeIsLoadingState = createSelector(
  gradesState,
  (grades) => grades.isLoading
);
export const loadedGradeState = createSelector(
  gradesState,
  (grades) => grades.grade
);
export const createdGradeState = createSelector(
  gradesState,
  (grades) => grades.created
);
export const loadedGradeTotalState = createSelector(
  gradesState,
  (grades) => grades.totalGrades
);
export const loadedGradeTableConfigState = createSelector(
  gradesState,
  (grades) => grades.gradesTableConfig
);

//Professional Fuel
export const professionalFuelState = (state: AppState) =>
  state.professionalFuel;
export const loadedProfessionalFuelData = createSelector(
  professionalFuelState,
  (professionalFuel) => professionalFuel.professionalFuelList
);
export const loadedProfessionalFuelIsLoadingState = createSelector(
  professionalFuelState,
  (professionalFuel) => professionalFuel.isLoading
);
export const loadedProfessionalFuelTotalState = createSelector(
  professionalFuelState,
  (professionalFuel) => professionalFuel.total
);
export const loadedProfessionalFuelGeneratedState = createSelector(
  professionalFuelState,
  (professionalFuel) => professionalFuel.generatedProfessionalFuelList
);
export const loadedProfessionalFuelTotalizerState = createSelector(
  professionalFuelState,
  (professionalFuel) => professionalFuel.totalizer
);
export const loadedProfessionalFuelDatesState = createSelector(
  professionalFuelState,
  (professionalFuel) => professionalFuel.dates
);
export const loadedProfessionalFuelCustomerState = createSelector(
  professionalFuelState,
  (professionalFuel) => professionalFuel.partyCode
);

//Tenant
export const tenantState = (state: AppState) => state.tenant;
export const loadedTenantState = createSelector(
  tenantState,
  (tenant) => tenant.tenant
);
export const loadedTenantConfigState = createSelector(
  tenantState,
  (tenant) => tenant.tenantConfig
);
export const getTenantLoadingTenantState = createSelector(
  tenantState,
  (tenant) => tenant.isLoading
);

//Products
export const productState = (state: AppState) => state.products;
export const loadedProductState = createSelector(
  productState,
  (products) => products.product
);
export const createdProductState = createSelector(
  productState,
  (products) => products.created
);
export const loadedProductListState = createSelector(
  productState,
  (products) => products.productList
);
export const loadedProductExcelListState = createSelector(
  productState,
  (products) => products.productExcelList
);
export const loadedProductTotalState = createSelector(
  productState,
  (products) => products.productTotal
);
export const loadedProductIsLoadingState = createSelector(
  productState,
  (products) => products.isLoading
);
export const loadedProductUnitListState = createSelector(
  productState,
  (products) => products.unitProducts
);
export const loadedProductTypesListState = createSelector(
  productState,
  (products) => products.productTypes
);
export const loadedProductFamilyListState = createSelector(
  productState,
  (products) => products.familyProducts
);
export const loadedProductGroupListState = createSelector(
  productState,
  (products) => products.groupProducts
);
export const loadedWarehouseProductsListState = createSelector(
  productState,
  (products) => products.warehouseProducts
);
export const loadedProductsTableConfig = createSelector(
  productState,
  (products) => products.productTableConfig
);
export const loadedProductsCategoriesState = createSelector(
  productState,
  (products) => products.categories
);
export const loadedProductsPropertiesState = createSelector(
  productState,
  (products) => products.properties
);
export const loadedProductSiteListState = createSelector(
  productState,
  (products) => products.siteList
);
export const loadedBarcodeTypesListState = createSelector(
  productState,
  (products) => products.barcodeTypes
);

//Product Group
export const groupsState = (state: AppState) => state.groups;
export const loadedProductGroupState = createSelector(
  groupsState,
  (groups) => groups.productGroup
);
export const createdProductGroupState = createSelector(
  groupsState,
  (groups) => groups.created
);
export const loadedProductGroupsListState = createSelector(
  groupsState,
  (groups) => groups.productGroupList
);
export const loadedProductGroupExcelListState = createSelector(
  groupsState,
  (groups) => groups.productGroupExcelList
);
export const loadedProductGroupIsLoading = createSelector(
  groupsState,
  (groups) => groups.isLoading
);
export const loadedProductGroupTotal = createSelector(
  groupsState,
  (groups) => groups.productGroupTotal
);
export const loadedProductGroupTableConfig = createSelector(
  groupsState,
  (groups) => groups.productGroupTableConfig
);
export const loadingProductGroupCategoriesState = createSelector(
  groupsState,
  (groups) => groups.categories
);
export const loadingProductGroupPropertiesState = createSelector(
  groupsState,
  (groups) => groups.properties
);

//Dictionary
export const dictionaryState = (state: AppState) => state.dictionary;
export const loadedDictionaryIsLoading = createSelector(
  dictionaryState,
  (dictionary) => dictionary.isLoading
);
export const loadedDictionaryListState = createSelector(
  dictionaryState,
  (dictionary) => dictionary.dictionaryList
);
export const loadedDictionaryExcelListState = createSelector(
  dictionaryState,
  (dictionary) => dictionary.dictionaryExcelList
);
export const loadedDictionaryTotalState = createSelector(
  dictionaryState,
  (dictionary) => dictionary.totalRecords
);
export const loadedDictionaryPropertiesForListState = createSelector(
  dictionaryState,
  (dictionary) => dictionary.propertiesList
);
export const loadedDictionaryTableConfig = createSelector(
  dictionaryState,
  (dictionary) => dictionary.dictionaryTableConfig
);
export const getDictionaryState = createSelector(
  dictionaryState,
  (dictionary) => dictionary.dictionary
);
export const loadedDictionaryPropertiesState = createSelector(
  dictionaryState,
  (dictionary) => dictionary.properties
);
export const loadedDictionaryCreatedState = createSelector(
  dictionaryState,
  (dictionary) => dictionary.created
);
export const loadedDictionaryDeletedState = createSelector(
  dictionaryState,
  (dictionary) => dictionary.deleted
);

//Service Mode
export const serviceModeState = (state: AppState) => state.serviceModes;
export const loadedServiceModesState = createSelector(
  serviceModeState,
  (serviceMode) => serviceMode.serviceModes
);
export const loadedExcelServiceModesState = createSelector(
  serviceModeState,
  (serviceMode) => serviceMode.serviceExcelModes
);
export const loadedServiceModeState = createSelector(
  serviceModeState,
  (serviceMode) => serviceMode.serviceMode
);
export const loadedServiceModeCreatedState = createSelector(
  serviceModeState,
  (serviceMode) => serviceMode.created
);
export const loadedServiceModeIsLoadingState = createSelector(
  serviceModeState,
  (serviceMode) => serviceMode.isLoading
);
export const loadedServiceModeTotalState = createSelector(
  serviceModeState,
  (serviceMode) => serviceMode.totalRecords
);
export const loadedServiceModeTableConfigState = createSelector(
  serviceModeState,
  (serviceMode) => serviceMode.serviceModeTableConfig
);

export const fuelingModeState = (state: AppState) => state.fuelingMode;
export const fuelingModeIsLoading = createSelector(
  fuelingModeState,
  (fuelingMode) => fuelingMode.isLoading
);
export const fuelingModeListState = createSelector(
  fuelingModeState,
  (fuelingMode) => fuelingMode.fuelingModeList
);
export const fuelingModeExcelListState = createSelector(
  fuelingModeState,
  (fuelingMode) => fuelingMode.fuelingModeExcelList
);
export const fuelingModeTotalState = createSelector(
  fuelingModeState,
  (fuelingMode) => fuelingMode.totalFuelingMode
);
export const fuelingModeTableConfigState = createSelector(
  fuelingModeState,
  (fuelingMode) => fuelingMode.fuelingModeTableConfig
);
export const loadedFuelingModeState = createSelector(
  fuelingModeState,
  (fuelingMode) => fuelingMode.fuelingMode
);
export const fuelingModeCreatedState = createSelector(
  fuelingModeState,
  (fuelingMode) => fuelingMode.created
);

export const tankGaugeState = (state: AppState) => state.tankGauge;
export const tankGaugeIsLoading = createSelector(
  tankGaugeState,
  (tankGauge) => tankGauge.isLoading
);
export const tankGaugeListState = createSelector(
  tankGaugeState,
  (tankGauge) => tankGauge.tankGauges
);

export const tankGaugesTotalState = createSelector(
  tankGaugeState,
  (tankGauge) => tankGauge.totalRecords
);
export const tankGaugesModeTableConfigState = createSelector(
  tankGaugeState,
  (tankGauge) => tankGauge.tankGaugeTableConfig
);
export const loadedTankGaugeState = createSelector(
  tankGaugeState,
  (tankGauge) => tankGauge.tankgauge
);
export const tankGaugeCreatedState = createSelector(
  tankGaugeState,
  (tankGauge) => tankGauge.created
);
export const tankGaugeWarehousesState = createSelector(
  tankGaugeState,
  (tankGauge) => tankGauge.warehouses
);
export const tankGaugeTypesState = createSelector(
  tankGaugeState,
  (tankGauge) => tankGauge.tankGaugeTypes
);

export const localesState = (state: AppState) => state.locales;
export const localesListState = createSelector(
  localesState,
  (locale) => locale.localesList
);
export const localesExcelListState = createSelector(
  localesState,
  (locale) => locale.localesExcel
);
export const localeState = createSelector(
  localesState,
  (locale) => locale.locale
);
export const localeIsLoadingState = createSelector(
  localesState,
  (locale) => locale.isLoading
);
export const localesTotalState = createSelector(
  localesState,
  (locale) => locale.totalLocales
);
export const localeCreatedState = createSelector(
  localesState,
  (locale) => locale.created
);
export const localeTableConfigState = createSelector(
  localesState,
  (locale) => locale.localesTableConfig
);
